// src/services/quoteService.js
// Quote Service — production grade, lineage-aware (Quote → Job → Invoice)
// Public flows now bypass the /api base so they hit /public/* correctly.

import api from '../api/axios';

/* --------------------------------- utils ---------------------------------- */
const pickPayload = (res) => (res?.data?.data ?? res?.data ?? {});
const toNum = (v, d = 0) => Number(v ?? d);

// derive the absolute backend ROOT (no /api suffix) from axios baseURL
const getBackendRoot = () => {
  const base = api?.defaults?.baseURL;
  try {
    if (base && /^https?:\/\//i.test(base)) {
      const u = new URL(base);
      return `${u.origin}`; // no trailing slash
    }
  } catch (_) {}
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return ''; // let axios resolve relative (node tests)
};

// low-friction axios call that ignores api.baseURL and Authorization for /public
const publicRequest = (method, url, data) => {
  const root = getBackendRoot();
  const full = `${root}${url}`; // absolute, so axios won’t prepend baseURL

  // Preserve Axios' default JSON transform so plain objects get JSON.stringified
  const defaultTx = api?.defaults?.transformRequest || [];
  const defaultArr = Array.isArray(defaultTx) ? defaultTx : [defaultTx];

  return api.request({
    method,
    url: full,
    data,
    withCredentials: false,
    transformRequest: [
      (body, headers) => {
        if (headers && 'Authorization' in headers) delete headers.Authorization;
        return body; // pass through to default transforms next
      },
      ...defaultArr,
    ],
  });
};

// normalize PDF peek shape from various backends
const normalizePdfPeek = (pdf = {}) => {
  const exists = Boolean(pdf.exists ?? pdf.url ?? pdf.key);
  return {
    exists,
    filename: pdf.filename || pdf.name || null,
    size: Number.isFinite(Number(pdf.size)) ? Number(pdf.size) : null,
    version: pdf.version ?? null,
    url: pdf.url || null,
    canAttach: pdf.canAttach != null ? !!pdf.canAttach : true,
  };
};

/* ----------------------------- item normalizer ----------------------------- */
const normalizeItems = (list = []) =>
  (Array.isArray(list) ? list : []).map((it) => {
    const qtyRaw = it.quantity ?? it.qty ?? 0;
    const qty = Number.isFinite(Number(qtyRaw)) ? Number(qtyRaw) : 0;

    // prefer explicit unitPrice/price; otherwise infer from total/qty
    const unitExplicit =
      it.unitPrice != null ? Number(it.unitPrice) :
      it.price     != null ? Number(it.price)     : null;

    let unit = unitExplicit;
    if (unit == null && qty > 0 && it.total != null) {
      const inferred = Number(it.total) / qty;
      unit = Number.isFinite(inferred) ? inferred : 0;
    }
    if (unit == null) unit = 0;

    // prefer explicit total; otherwise qty * unit
    let total =
      it.total != null ? Number(it.total) :
      (Number.isFinite(qty) && Number.isFinite(unit) ? qty * unit : 0);

    if (!Number.isFinite(total)) total = 0;

    return {
      name: it.name || it.title || 'Item',
      description: it.description || it.desc || '',
      quantity: qty,
      unitPrice: unit,
      total,
      _raw: it,
    };
  });

/* ----------------------------- normalizers ----------------------------- */
export const normalizeQuote = (q = {}) => {
  const id = q._id || q.id || q.quoteId;

  const lifecycle =
    (q.lifecycleStatus || q.lifecycle || q.status || '')
      .toString()
      .toLowerCase() || 'draft';

  const workObj = q.work || q.job || null;
  const invoiceObj = q.invoice || null;

  const workId = q.workId || workObj?._id || workObj?.id || null;
  const workNumber = q.workNumber || workObj?.workNumber || workObj?.workNo || null;

  const invoiceId = q.invoiceId || invoiceObj?._id || invoiceObj?.id || null;
  const invoiceNumber =
    q.invoiceNumber || invoiceObj?.invoiceNumber || invoiceObj?.number || null;

  const currency = (q.currency || 'USD').toUpperCase();

  const totals = q.totals || {};
  const subtotal = toNum(q.subtotal ?? totals.subtotal, 0);
  const taxTotal = toNum(q.taxTotal ?? q.taxAmount ?? totals.taxTotal, 0);
  const discountTotal = toNum(q.discountTotal ?? q.discountAmount ?? totals.discountTotal, 0);
  const totalAmount = toNum(q.totalAmount ?? totals.totalAmount ?? q.total, 0);

  const lineItems = normalizeItems(q.lineItems || q.items || []);

  return {
    ...q,
    id, _id: id, quoteId: id,
    lifecycleStatus: lifecycle,
    issueDate: q.issueDate || q.issuedAt || q.createdAt || null,
    expiryDate: q.expiryDate || q.validUntil || null,
    currency,
    subtotal, taxTotal, discountTotal, totalAmount,
    lineItems,
    workId, workNumber, invoiceId, invoiceNumber,
  };
};

const parseListResponse = (res) => {
  const payload = res?.data;
  const raw = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload)
    ? payload
    : [];

  const meta =
    payload?.meta || {
      page: Number(payload?.page || 1),
      limit: Number(payload?.limit || raw.length || 25),
      total: Number(payload?.total || raw.length || 0),
      totalPages: Number(
        payload?.totalPages ||
          (payload?.total
            ? Math.ceil(Number(payload.total) / Number(payload.limit || 25))
            : 1)
      ),
    };

  return { data: raw.map(normalizeQuote), meta };
};

/* ----------------------------- CRUD-ish ----------------------------- */
export const listQuotes = async (params = {}) => {
  const relationHints = {
    include: ['client', 'work', 'invoice'],
    populate: ['client', 'work', 'invoice'],
    expand: ['client', 'work', 'invoice'],
  };
  const res = await api.get('/quotes', { params: { ...relationHints, ...params } });
  return parseListResponse(res);
};

export const getQuote = async (id) => {
  const res = await api.get(`/quotes/${id}`, {
    params: {
      include: ['client', 'work', 'invoice'],
      populate: ['client', 'work', 'invoice'],
      expand: ['client', 'work', 'invoice'],
    },
  });
  const q = res?.data?.data || res?.data || null;
  return q ? normalizeQuote(q) : null;
};

export const createQuote = async (payload) => {
  const res = await api.post('/quotes', payload);
  const q = pickPayload(res);
  return q ? normalizeQuote(q) : null;
};

export const updateQuote = async (id, payload) => {
  const res = await api.put(`/quotes/${id}`, payload);
  const q = pickPayload(res);
  return q ? normalizeQuote(q) : null;
};

export const deleteQuote = async (id) => {
  await api.delete(`/quotes/${id}`);
  return true;
};

/* ----------------------------- Lifecycle actions ----------------------------- */
export const acceptQuote = async (id, body = {}) => {
  const res = await api.post(`/quotes/${id}/accept`, body);
  const q = pickPayload(res);
  return q ? normalizeQuote(q) : null;
};

export const declineQuote = async (id, body = {}) => {
  const res = await api.post(`/quotes/${id}/decline`, body);
  const q = pickPayload(res);
  return q ? normalizeQuote(q) : null;
};

export const cancelQuote = async (id, body = {}) => {
  const res = await api.post(`/quotes/${id}/cancel`, body);
  const q = pickPayload(res);
  return q ? normalizeQuote(q) : null;
};

export const archiveQuote = async (id, body = {}) => {
  const res = await api.post(`/quotes/${id}/archive`, body);
  const q = pickPayload(res);
  return q ? normalizeQuote(q) : true;
};

/* ----------------------------- Phase 5.2 — Operator send ----------------------------- */
export const prepareSend = async (id) => {
  const res = await api.get(`/quotes/${id}/send/prepare`);
  const data = pickPayload(res);
  return { ...data, pdf: normalizePdfPeek(data.pdf || {}) };
};

export const sendQuote = async (id, payload = {}) => {
  try {
    const res = await api.post(`/quotes/${id}/send`, payload);
    const data = pickPayload(res);
    const status = data.status || (res?.status === 202 ? 'queued' : 'ok');
    const queued =
      typeof data.queued === 'boolean'
        ? data.queued
        : (res?.status === 202 || status === 'queued');

    return {
      ok: true,
      queued,
      status,
      messageKey: data.messageKey ?? data.id ?? null,
      providerMessageId: data.providerMessageId ?? null,
      quote: data.quote ? normalizeQuote(data.quote) : undefined,
      raw: data,
    };
  } catch (e) {
    const code = e?.response?.status;
    if (code === 409) {
      const data = pickPayload(e.response);
      return {
        ok: true,
        queued: false,
        status: 'duplicate',
        alreadySent: true,
        retryAfter: Number(e.response?.headers?.['retry-after'] || 0) || null,
        raw: data,
      };
    }
    if (code === 429) {
      const data = pickPayload(e.response);
      return {
        ok: false,
        rateLimited: true,
        status: 'rate_limited',
        retryAfter: Number(e.response?.headers?.['retry-after'] || 0) || null,
        raw: data,
      };
    }
    throw e;
  }
};

/* ----------------------------- Phase 5.2 — Public flows ----------------------------- */
// Uses absolute ROOT to avoid '/api' prefix. No auth header.

export const fetchPublicQuote = async (token) => {
  const res = await publicRequest('get', `/public/quote/${encodeURIComponent(token)}`);
  const data = pickPayload(res);
  const rawQuote = data.quote ?? data;
  const quote = rawQuote ? normalizeQuote(rawQuote) : null;
  return { quote, meta: data.meta ?? null, raw: data };
};

export const decidePublicQuote = async (token, { decision, reason } = {}) => {
  const d = String(decision || '').toLowerCase();
  if (!['accept', 'decline'].includes(d)) {
    throw new Error("decision must be 'accept' or 'decline'");
  }
  const body = reason ? { decision: d, reason } : { decision: d };
  const res = await publicRequest(
    'post',
    `/public/quote/${encodeURIComponent(token)}/decision`,
    body
  );
  const data = pickPayload(res);
  const quotePayload = data.quote ?? data.updated ?? data;
  const quote = quotePayload && (quotePayload._id || quotePayload.id)
    ? normalizeQuote(quotePayload)
    : undefined;
  return { decision: d, quote, raw: data };
};

// Convenience wrappers for UI buttons
export const acceptPublicQuote = async (token, reason) =>
  decidePublicQuote(token, { decision: 'accept', reason });

export const declinePublicQuote = async (token, reason) =>
  decidePublicQuote(token, { decision: 'decline', reason });

/* ----------------------------- Conversions ----------------------------- */
export const convertToInvoice = async (id, body = {}) => {
  const candidates = [
    `/quotes/${id}/convert`,
    `/quotes/${id}/convert-to-invoice`,
    `/quotes/${id}/convertToInvoice`,
    `/invoices/from-quote/${id}`,
    `/invoices/convert-from-quote/${id}`,
    `/invoices/fromQuote/${id}`,
  ];
  const tryPost = async (path) => {
    try { return await api.post(path, body); }
    catch (e) {
      const code = e?.response?.status;
      if (code === 404 || code === 405 || code === 501) return null;
      throw e;
    }
  };
  let res = null;
  for (const p of candidates) { // eslint-disable-next-line no-await-in-loop
    const attempt = await tryPost(p); if (attempt) { res = attempt; break; }
  }
  if (!res) throw new Error('No quote→invoice conversion endpoint found on the server.');
  const payload = pickPayload(res);

  let invoiceId =
    payload.invoiceId ?? payload.id ?? payload._id ??
    payload.invoice?.id ?? payload.invoice?._id;

  if (!invoiceId && res?.headers?.location) {
    const parts = String(res.headers.location).split('/').filter(Boolean);
    invoiceId = parts[parts.length - 1];
  }

  const invoiceNumber =
    payload.invoiceNumber ?? payload.number ??
    payload.invoice?.invoiceNumber ?? payload.invoice?.number ?? null;

  const workId = payload.workId ?? payload.work?._id ?? payload.work?.id ?? null;
  const workNumber = payload.workNumber ?? payload.work?.workNumber ?? payload.work?.workNo ?? null;

  return {
    quoteId: id || payload.quoteId || null,
    invoiceId: invoiceId || null,
    invoiceNumber,
    workId,
    workNumber,
    raw: payload,
  };
};

export const convertToJob = async (id, body = {}) => {
  const candidates = [
    `/quotes/${id}/convert-to-job`,
    `/quotes/${id}/convertToJob`,
    `/work/from-quote/${id}`,
    `/works/from-quote/${id}`,
    `/jobs/from-quote/${id}`,
    `/work/fromQuote/${id}`,
  ];
  const tryPost = async (path) => {
    try { return await api.post(path, body); }
    catch (e) {
      const code = e?.response?.status;
      if (code === 404 || code === 405 || code === 501) return null;
      throw e;
    }
  };
  let res = null;
  for (const p of candidates) { // eslint-disable-next-line no-await-in-loop
    const attempt = await tryPost(p); if (attempt) { res = attempt; break; }
  }
  if (!res) throw new Error('No quote→job conversion endpoint found on the server.');
  const payload = pickPayload(res);

  const workId =
    payload.workId ?? payload.id ?? payload._id ??
    payload.work?._id ?? payload.work?.id ?? null;

  const workNumber =
    payload.workNumber ?? payload.work?.workNumber ?? payload.work?.workNo ?? null;

  return {
    quoteId: id || payload.quoteId || null,
    workId: workId || null,
    workNumber,
    raw: payload,
  };
};
